import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import Button from '../common/Button';
import { usePrefersReducedMotion } from '../../hooks/useReveal';
import './HeroPremium.css';

const HeroScene = lazy(() => import('../fx/HeroScene'));

const ROTATING = ['Web Platforms', 'Mobile Apps', 'SaaS Products', 'AI Solutions', 'Cloud Systems', 'Enterprise Apps'];

const TERMINAL = [
  { t: 'cmd', v: 'qs deploy --env production' },
  { t: 'ok', v: 'build passed', m: '1.8s' },
  { t: 'ok', v: '428 tests passed', m: '0 failed' },
  { t: 'ok', v: 'rolling release', m: 'example' },
  { t: 'ok', v: 'health checks green', m: '99.99%' },
];

function canUseWebGL() {
  try {
    const c = document.createElement('canvas');
    return !!(window.WebGLRenderingContext && (c.getContext('webgl2') || c.getContext('webgl')));
  } catch {
    return false;
  }
}

function useRotatingWord(words, paused) {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (paused) return undefined;
    const id = setInterval(() => setI((n) => (n + 1) % words.length), 2600);
    return () => clearInterval(id);
  }, [words.length, paused]);
  return words[i];
}

function Hero() {
  const reduced = usePrefersReducedMotion();
  const [webgl, setWebgl] = useState(false);
  const [lite, setLite] = useState(false);
  const word = useRotatingWord(ROTATING, reduced);
  const rootRef = useRef(null);
  const [visible, setVisible] = useState(true);

  /* Pause the WebGL loop while the hero is scrolled out of view */
  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(([e]) => setVisible(e.isIntersecting), { threshold: 0 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const saveData = navigator.connection?.saveData;
    const small = window.innerWidth < 768;
    setLite(small || (navigator.hardwareConcurrency || 8) <= 4);
    setWebgl(!saveData && canUseWebGL());
  }, []);

  return (
    <section className="qh-root" aria-labelledby="qh-heading" ref={rootRef}>
      <div className="qh-bg" aria-hidden="true">
        <div className="qh-grid" />
        <div className="qh-orb qh-orb--1" />
        <div className="qh-orb qh-orb--2" />
      </div>

      <div className="qh-canvas" aria-hidden="true">
        {webgl && (
          <Suspense fallback={null}>
            <HeroScene reduced={reduced} lite={lite} active={visible} />
          </Suspense>
        )}
        {!webgl && <div className="qh-fallback-core" />}
      </div>

      <div className="qh-inner">
        <motion.div
          className="qh-copy"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="qh-eyebrow">
            <span className="qh-pulse" aria-hidden="true" />
            Software Engineering &amp; Digital Solutions — Pune, India
          </p>

          <h1 className="qh-title" id="qh-heading">
            <span className="visually-hidden">Quantum Stack — software development company in Pune, India. </span>
            We engineer
            <span className="qh-rotator" aria-live="polite">
              <motion.span
                key={word}
                className="qh-rotator__word"
                initial={{ opacity: 0, y: 18, filter: 'blur(6px)' }}
                animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                transition={{ duration: 0.5 }}
              >
                {word}
              </motion.span>
            </span>
            that scale.
          </h1>

          <p className="qh-sub">
            Quantum Stack is your technology partner for building reliable digital
            products. Share your requirements — we architect, build, launch and
            support web &amp; mobile apps, SaaS, cloud and AI solutions that fit your
            business, on the technology that suits your goals.
          </p>

          <nav className="qh-actions" aria-label="Primary actions">
            <Button to="/contact" size="lg" className="qh-btn-glow">
              Get a Free Consultation
            </Button>
            <Button to="/services" variant="outline-light" size="lg">
              See Our Services
            </Button>
          </nav>

          <ul className="qh-chips" aria-label="Highlights">
            <li>Built around your requirements</li>
            <li>Any tech stack</li>
            <li>Fixed-price or dedicated team</li>
            <li>You own the code</li>
          </ul>
        </motion.div>

        <div className="qh-stage" aria-hidden="true">
          <motion.div
            className="qh-term"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.7 }}
          >
            <div className="qh-term__bar">
              <i /><i /><i />
              <span>illustrative example — deploy pipeline</span>
            </div>
            <div className="qh-term__body">
              {TERMINAL.map((l, n) => (
                <p key={n} className={`qh-term__line qh-term__line--${l.t}`} style={{ animationDelay: `${1.0 + n * 0.55}s` }}>
                  {l.t === 'cmd' ? <><b>$</b> {l.v}</> : <><b>✓</b> {l.v}<em>{l.m}</em></>}
                </p>
              ))}
              <p className="qh-term__line qh-term__line--cmd" style={{ animationDelay: '4.2s' }}><b>$</b> <span className="qh-term__caret" /></p>
            </div>
          </motion.div>
        </div>
      </div>

      <a href="#after-hero" className="qh-scroll" aria-label="Scroll to content">
        <span className="qh-scroll__mouse"><i /></span>
        Scroll
      </a>
    </section>
  );
}

export default Hero;
